import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import useErrorModal from 'hooks/use-error-modal';
import { t } from 'state/intl/translations';

import { usePaymentContext } from '../state';

import { AddEditPaymentMethod, PaymentMethodRef } from './add-payment-method-modal/add-new-method';

export function AddPaymentMethodRouteModal() {
  const addMethodRef = useRef<PaymentMethodRef | null>(null);
  const hasDismissedRef = useRef(false);
  const isRouteActiveRef = useRef(true);
  const router = useRouter();
  const { accountToDelete } = useLocalSearchParams<{ accountToDelete?: string }>();
  const { setAccountToDelete, setIsQuickPayMethodSelected } = usePaymentContext();
  const [isLoading, setIsLoading] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Credit/Debit Card',
  });

  useEffect(() => {
    hasDismissedRef.current = false;
    isRouteActiveRef.current = true;
    setAccountToDelete(accountToDelete ?? '');

    return () => {
      isRouteActiveRef.current = false;
      setAccountToDelete('');
      if (!hasDismissedRef.current) {
        hasDismissedRef.current = true;
        setIsQuickPayMethodSelected(false);
      }
    };
  }, [accountToDelete, setAccountToDelete, setIsQuickPayMethodSelected]);

  const shouldHandleAsyncResult = useCallback(
    () => isRouteActiveRef.current && !hasDismissedRef.current,
    []
  );

  const handleDismiss = useCallback(() => {
    if (!shouldHandleAsyncResult()) {
      return;
    }

    hasDismissedRef.current = true;
    setIsQuickPayMethodSelected(false);
    router.dismiss();
  }, [router, setIsQuickPayMethodSelected, shouldHandleAsyncResult]);

  return (
    <CardModal
      analyticsEvent="Add payment method"
      footer={
        <ActionButton
          disabled={!canSubmit || isLoading}
          fullWidth
          isLoading={isLoading}
          onPress={() => addMethodRef.current?.addNewCard()}
          testID="save-and-continue"
        >
          {t('continue')}
        </ActionButton>
      }
      headerText={accountToDelete ? t('updateCard') : t('addCreditDebitCard')}
      keyboardAwareFooter
      onDismiss={handleDismiss}
      scrollView
      standardPadding
      testID="add-payment-modal-heading"
    >
      <AddEditPaymentMethod
        isLoading={isLoading}
        isPrepaidReload={false}
        mode="vaulted"
        onCompleted={handleDismiss}
        openErrorDialog={openErrorDialog}
        ref={addMethodRef}
        setCanSubmit={setCanSubmit}
        setIsLoading={setIsLoading}
        shouldHandleAsyncResult={shouldHandleAsyncResult}
      />
      <ErrorDialog />
    </CardModal>
  );
}
